'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ApprovalsTabById from 'src/parts/tabs/ApprovalsTabById';
import { getUserApprovals } from 'src/api/admin/approvals';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/users/$userId/approvals/')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const approvals = await getUserApprovals(userId, deps);
		return { approvals };
	},
	component: ApprovalsTab,
});

function ApprovalsTab() {
	const { userId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const searchParams = Route.useSearch();
	const { approvals: initialApprovals } = Route.useLoaderData();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const { data: approvals, refetch: refetchApprovals } = useQuery({
		queryKey: ['user-approvals', userId, searchParams],
		queryFn: () => getUserApprovals(userId, searchParams),
		initialData: initialApprovals,
	});

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
		refetchApprovals();
	};

	const handleReload = () => {
		refetchApprovals();
	};

	const handleApprovalSelected = (approvalId: string) => {
		navigate({ to: `/users/$userId/approvals/$approvalId`, params: { userId, approvalId } });
	};

	return (
		<ApprovalsTabById
			approvals={approvals}
			authenticatedUser={authenticatedUser}
			onUpdate={handleUpdate}
			onReload={handleReload}
			onApprovalSelected={handleApprovalSelected}
		/>
	);
}
